import { useEffect, useState } from 'react';

/**
 * useMediaQuery — vrai tant que la requête média correspond.
 *
 * Écrit pour un cas précis : le bandeau d'échéance de la page dossier.
 * Le design le place dans le rail ; sous 1100 px le rail passe SOUS le
 * contenu principal, et l'échéance — l'information la plus urgente de
 * l'écran, souvent un délai légal — se retrouvait à quatre mille pixels
 * du haut de la page.
 *
 * `order: -1` ne suffit pas : il réordonne À L'INTÉRIEUR du rail, pas
 * entre le rail et la colonne principale. Il n'existe pas de règle CSS
 * qui sorte un petit-enfant de son conteneur. Le composant est donc
 * rendu à l'un ou l'autre endroit selon la largeur — un seul nœud dans
 * le DOM, jamais deux (un `role="status"` dupliqué serait annoncé deux
 * fois par un lecteur d'écran).
 */
export default function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    // La valeur peut avoir changé entre le premier rendu et l'effet
    // (rotation, redimensionnement pendant le chargement).
    setMatches(mql.matches);
    // `addEventListener` sur MediaQueryList n'existe pas sur les Safari
    // anciens ; `addListener` y répond encore.
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}
